/**
 * DesktopAuthBridge — Reshapes V1 user context into V2's useCurrentUser() shape.
 *
 * The V1 shell stores user data in its own context (src/providers/UserProvider)
 * with a different interface shape than V2's CurrentUserContextValue. This bridge
 * reads from V1 and provides the V2 context so shared V2 hooks/components
 * (useCurrentUser, usePermissions, etc.) work without modification.
 *
 * When we move to the unified shell (Phase 2), this file is deleted.
 */
import React, { useContext, useMemo } from 'react';
import { useUserDetails } from 'src/providers/UserProvider';
import { usePreferredLocationValue } from 'src/v2/shared/data/preferred-location';
import {
  CurrentUserContext,
  APP_PROGRAM_ID,
  type CurrentUserContextValue,
} from 'src/v2/shared/auth';

interface DesktopAuthBridgeProps {
  readonly children: React.ReactNode;
}

/**
 * Bridges V1 user context → V2 CurrentUserContextValue.
 *
 * V1 guarantees that user, preferences, metadata, and permissions are all
 * defined by the time child components render (the V1 UserProvider gates
 * on all of them). So we can safely map them without null checks.
 */
export function DesktopAuthBridge({ children }: DesktopAuthBridgeProps): JSX.Element {
  const { user, userPreferences, userMetadata, permissions } = useUserDetails();

  // alias === Midway sub claim. V1's UserProvider fetches userMetadata with
  // the Midway alias as userId, and the backend echoes it back in the response.
  // This is the same value V2's useMidway() returns as userAlias.
  const alias = userMetadata?.userId ?? '';
  const businessUnit = userPreferences?.profileSettings?.businessUnit;

  const preferredLocation = usePreferredLocationValue(alias, businessUnit);

  const savedLocations: readonly string[] = useMemo(
    () =>
      userPreferences?.profileSettings?.businessUnitPreferences?.find(
        (entry) => entry.businessUnit === businessUnit,
      )?.locations ?? [],
    [userPreferences, businessUnit],
  );

  const value = useMemo<CurrentUserContextValue>(
    () => ({
      alias,
      programId: APP_PROGRAM_ID,
      user,
      userMetadata,
      userPreferences,
      permissions,
      businessUnit,
      preferredLocation,
      savedLocations,
    }),
    [
      alias,
      user,
      userMetadata,
      userPreferences,
      permissions,
      businessUnit,
      preferredLocation,
      savedLocations,
    ],
  );

  return (
    <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>
  );
}

/**
 * V2's useCurrentUser for the desktop hybrid context.
 * Falls through to the bridged context when rendered inside V2Adapter.
 */
export function useDesktopCurrentUser(): CurrentUserContextValue {
  const value = useContext(CurrentUserContext);
  if (value === null) {
    throw new Error('useDesktopCurrentUser must be used within <DesktopAuthBridge>');
  }
  return value;
}
